import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import ServicePageLayout from '../../components/Layout/ServicePageLayout';
import { FaChartLine, FaBullhorn, FaSearch, FaUsers, FaEnvelope, FaHashtag } from 'react-icons/fa';
import { SiGoogleads, SiMeta, SiGoogleanalytics, SiInstagram } from 'react-icons/si';
import { MdEmail, MdPhone, MdPerson, MdMessage } from 'react-icons/md';
import { submitToGoogleSheet } from '../../utils/helpers';
import classes from './ServicePage.module.scss';
import contactClasses from '../../components/Contact/Contact.module.scss';
import FAQ from '../../components/FAQ/FAQ';
import digitalMarketingHeroImg from '../../assets/web_dev_hero_img.png';
import digitalMarketingPortfolio1 from '../../assets/artist_next_door_web_portfolio.png';
import digitalMarketingPortfolio2 from '../../assets/guruganeshsaree_portfolio.png';
import seoFeature from '../../assets/web_dev_seo_feature.png';
import analyticsFeature from '../../assets/web_dev_fast_performance_feature.png';
import socialMediaFeature from '../../assets/web_dev_responsive_feature.png';
import contentMarketingFeature from '../../assets/web_dev_scalable_feature.png';

const DigitalMarketing = () => {
  const { projects } = useSelector((state) => state.portfolio);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    message: '',
  });

  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitToGoogleSheet(formData);
      if (typeof window.gtag_report_conversion === 'function') {
        window.gtag_report_conversion();
      }
      alert('Thank you! We will get back to you soon.');
      setFormData({ name: '', email: '', phoneNumber: '', message: '' });
    } catch (error) {
      console.error('Form submission error:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % marketingProjects.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + marketingProjects.length) % marketingProjects.length);
  };

  const marketingProjects = [
    {
      id: 1,
      title: 'ARTIST PLATFORM GROWTH',
      subtitle: 'Social Media & SEO Campaign',
      description: 'Increased organic traffic by 300% and social engagement by 450% through strategic digital marketing campaigns across multiple platforms.',
      image: digitalMarketingPortfolio1,
      category: 'Marketing',
      link: '/services/digital-marketing',
      bgGradient: 'linear-gradient(135deg, rgb(133, 89, 200) 0%, rgb(52, 29, 89) 100%)'
    },
    {
      id: 2,
      title: 'E-COMMERCE SUCCESS',
      subtitle: 'Paid Ads & Conversion Optimization',
      description: 'Achieved 5x ROI through targeted Google Ads and Meta campaigns, with optimized conversion funnels and retargeting strategies.',
      image: digitalMarketingPortfolio2,
      category: 'Marketing',
      link: '/services/digital-marketing',
      bgGradient: 'linear-gradient(135deg, rgb(198, 170, 125) 0%, rgb(200, 175, 140) 100%)'
    }
  ];

  const services = [
    { name: 'SEO & SEM', icon: <FaSearch />, description: 'Search engine optimization & marketing' },
    { name: 'Social Media', icon: <FaHashtag />, description: 'Multi-platform social campaigns' },
    { name: 'Google Ads', icon: <SiGoogleads />, description: 'Pay-per-click advertising' },
    { name: 'Analytics', icon: <SiGoogleanalytics />, description: 'Data-driven insights & reporting' },
  ];

  const features = [
    {
      title: 'SEO Optimization',
      description: 'Boost your search rankings with proven SEO strategies, keyword research, and technical optimization.',
      image: seoFeature
    },
    {
      title: 'Social Media Marketing',
      description: 'Engage your audience across Instagram, Facebook, LinkedIn, and Twitter with compelling content.',
      image: socialMediaFeature
    },
    {
      title: 'Data Analytics',
      description: 'Track performance, measure ROI, and make data-driven decisions with comprehensive analytics.',
      image: analyticsFeature
    },
    {
      title: 'Content Marketing',
      description: 'Create valuable content that attracts, engages, and converts your target audience.',
      image: contentMarketingFeature
    }
  ];

  const faqs = [
    {
      question: 'How long does it take to see results from digital marketing?',
      answer: 'SEO typically shows results in 3-6 months, while paid ads can drive traffic immediately. Social media growth is gradual but sustainable, usually showing traction within 2-3 months of consistent effort.'
    },
    {
      question: 'What platforms do you specialize in?',
      answer: 'We specialize in Google Ads, Meta (Facebook & Instagram), LinkedIn, Twitter, TikTok, and YouTube. We also handle SEO, content marketing, email campaigns, and conversion rate optimization.'
    },
    {
      question: 'How do you measure marketing success?',
      answer: 'We track key metrics like traffic growth, conversion rates, ROI, engagement rates, lead quality, and customer acquisition costs. You receive monthly reports with actionable insights and performance analysis.'
    },
    {
      question: 'Do you create content or just run ads?',
      answer: 'We offer full-service digital marketing — strategy, content creation (graphics, videos, copy), ad management, SEO, social media management, and analytics. You can choose individual services or comprehensive packages.'
    },
    {
      question: 'What is your pricing model?',
      answer: 'We offer flexible pricing — monthly retainers for ongoing campaigns, project-based pricing for specific initiatives, or performance-based models for growth-focused engagements. Pricing depends on scope, platforms, and ad spend.'
    },
    {
      question: 'Can you help with both organic and paid strategies?',
      answer: 'Absolutely. We combine organic strategies (SEO, content marketing, social media) with paid advertising (Google Ads, Meta Ads) for maximum reach and sustainable growth. The ideal mix depends on your goals and budget.'
    },
  ];

  const process = [
    { step: '01', title: 'Market Research', description: 'Analyzing your industry, competitors, and target audience' },
    { step: '02', title: 'Strategy Development', description: 'Creating a data-driven marketing roadmap' },
    { step: '03', title: 'Content Creation', description: 'Designing compelling ads, posts, and campaigns' },
    { step: '04', title: 'Campaign Launch', description: 'Executing across selected channels and platforms' },
    { step: '05', title: 'Optimization', description: 'A/B testing and continuous improvement' },
    { step: '06', title: 'Reporting & Analysis', description: 'Measuring results and refining strategies' },
  ];

  return (
    <ServicePageLayout>
      <div className={classes.servicePageContainer}>
        {/* Hero Section */}
        <section className={classes.hero}>
          <div className={classes.container}>
            <div className={classes.heroContent}>
              <div className={classes.heroHighlight}>
                <div className={classes.heroText}>

                  <h1 className={classes.heroTitle}>
                    Digital Marketing <span className={classes.gradient}>Services</span>
                  </h1>
                  <p className={classes.heroSubtitle}>
                    Drive growth with data-driven digital marketing strategies. From SEO and social media
                    to paid advertising and analytics, we help you reach and convert your target audience.
                  </p>
                  <div className={classes.heroCTA}>
                    <button
                      className={classes.exploreButton}
                      onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                    >
                      Explore Services
                    </button>
                    <button
                      className={classes.quoteButton}
                      onClick={() => document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' })}
                    >
                      Get A Quote
                    </button>
                  </div>
                  <div className={classes.heroStats}>
                    <div className={classes.stat}>
                      <span className={classes.statNumber}>5x</span>
                      <span className={classes.statLabel}>Average ROI</span>
                    </div>
                    <div className={classes.stat}>
                      <span className={classes.statNumber}>300%</span>
                      <span className={classes.statLabel}>Traffic Growth</span>
                    </div>
                    <div className={classes.stat}>
                      <span className={classes.statNumber}>50+</span>
                      <span className={classes.statLabel}>Campaigns Launched</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className={classes.heroVisual}>
                <div className={classes.phoneContainer}>
                  <img
                    src={digitalMarketingHeroImg}
                    alt="Digital Marketing"
                    className={classes.phoneImage}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>


        {/* Features Section */}
        <section id="features" className={classes.features}>
          <div className={classes.container}>
            <div className={classes.sectionHeader}>
              <h2 className={classes.sectionTitle}>
                Why Choose Our <span className={classes.gradient}>Digital Marketing</span>
              </h2>
            </div>
            <div className={classes.featuresGrid}>
              {features.map((feature, index) => (
                <div key={index} className={classes.featureCard}>
                  <div className={classes.featureImageContainer}>
                    <img
                      src={feature.image}
                      alt={feature.title}
                      className={classes.featureImage}
                    />
                  </div>
                  <h3 className={classes.featureTitle}>{feature.title}</h3>
                  <p className={classes.featureDescription}>{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>


        {/* Services Section */}
        <section className={classes.technologies}>
          <div className={classes.container}>
            <div className={classes.sectionHeader}>
              <h2 className={classes.sectionTitle}>
                Our Marketing <span className={classes.gradient}>Services</span>
              </h2>
              <p className={classes.sectionSubtitle}>
                Comprehensive digital marketing solutions to grow your brand
              </p>
            </div>
            <div className={classes.techGrid}>
              {services.map((service, index) => (
                <div key={index} className={classes.techCard}>
                  <div className={classes.techIcon}>{service.icon}</div>
                  <h3 className={classes.techName}>{service.name}</h3>
                  <p className={classes.techDescription}>{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Projects Section */}
        <section className={classes.portfolioSection}>
          <div className={classes.portfolioContainer}>
            <div className={classes.portfolioHeader}>
              <h2 className={classes.portfolioTitle}>
                Marketing <span className={classes.gradient}>Success Stories</span>
              </h2>
            </div>

            <div className={classes.carousel}>
              <div className={classes.slideContainer}>
                <div
                  className={classes.slides}
                  style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                >
                  {marketingProjects.map((project, index) => (
                    <div
                      key={project.id}
                      className={classes.slide}
                      style={{ background: project.bgGradient }}
                    >
                      <div className={classes.slideContent}>
                        <div className={classes.slideTop}>
                          <div className={classes.imageContainer}>
                            <img src={project.image} alt={project.title} />
                          </div>
                        </div>

                        <div className={classes.slideBottom}>
                          <div className={classes.slideHeader}>
                            <h3 className={classes.projectTitle}>{project.title}</h3>
                            <span className={classes.categoryBadge}>{project.category}</span>
                          </div>

                          <h4 className={classes.projectSubtitle}>{project.subtitle}</h4>

                          <p className={classes.projectDescription}>{project.description}</p>

                          <a href={project.link} className={classes.learnMoreBtn}>
                            <span>Learn More</span>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className={classes.navigation}>
                <button
                  className={`${classes.navBtn} ${classes.prevBtn}`}
                  onClick={prevSlide}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>

                <button
                  className={`${classes.navBtn} ${classes.nextBtn}`}
                  onClick={nextSlide}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>

              <div className={classes.indicators}>
                {marketingProjects.map((_, index) => (
                  <button
                    key={index}
                    className={`${classes.indicator} ${index === currentSlide ? classes.active : ''}`}
                    onClick={() => setCurrentSlide(index)}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className={classes.process}>
          <div className={classes.container}>
            <div className={classes.sectionHeader}>
              <h2 className={classes.sectionTitle}>
                Our Marketing <span className={classes.gradient}>Process</span>
              </h2>
              <p className={classes.sectionSubtitle}>
                A systematic approach to delivering measurable marketing results
              </p>
            </div>
            <div className={classes.processGrid}>
              {process.map((step, index) => (
                <div key={index} className={classes.processStep}>
                  <div className={classes.stepNumber}>{step.step}</div>
                  <h3 className={classes.stepTitle}>{step.title}</h3>
                  <p className={classes.stepDescription}>{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <FAQ faqs={faqs} title="Frequently Asked" highlight="Questions" />

        {/* Contact Form Section */}
        <section id="contact-form" className={contactClasses.contact}>
          <div className={contactClasses.container}>
            <div className={contactClasses.header}>
              <span className={contactClasses.badge}>Get Your Quote</span>
              <h2 className={contactClasses.title}>
                Let's Grow Your <span className={contactClasses.gradient}>Brand Together</span>
              </h2>
            </div>

            <div className={contactClasses.content}>
              <form className={contactClasses.form} onSubmit={handleSubmit}>
                <div className={contactClasses.formRow}>
                  <div className={contactClasses.formGroup}>
                    <label htmlFor="name">
                      <MdPerson className={contactClasses.labelIcon} />
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Enter your full name"
                      className={contactClasses.premiumInput}
                    />
                  </div>

                  <div className={contactClasses.formGroup}>
                    <label htmlFor="email">
                      <MdEmail className={contactClasses.labelIcon} />
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your.email@company.com"
                      className={contactClasses.premiumInput}
                    />
                  </div>
                </div>

                <div className={contactClasses.formGroup}>
                  <label htmlFor="phoneNumber">
                    <MdPhone className={contactClasses.labelIcon} />
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    required
                    placeholder="+1 (555) 123-4567"
                    className={contactClasses.premiumInput}
                  />
                </div>

                <div className={contactClasses.formGroup}>
                  <label htmlFor="message">
                    <MdMessage className={contactClasses.labelIcon} />
                    Marketing Goals & Requirements
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    placeholder="Tell us about your marketing goals, target audience, current challenges, and what you're looking to achieve..."
                    className={contactClasses.premiumTextarea}
                  ></textarea>
                </div>

                <button type="submit" className={contactClasses.submitBtn} disabled={loading}>
                  <span className={contactClasses.btnText}>
                    {loading && <span className={contactClasses.spinner} />}
                    {loading ? 'Sending...' : 'Get My Quote'}
                  </span>
                  <div className={contactClasses.btnGlow}></div>
                </button>
              </form>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className={classes.cta}>
          <div className={classes.container}>
            <div className={classes.ctaContent}>
              <h2 className={classes.ctaTitle}>
                Ready to Boost Your <span className={classes.gradient}>Digital Presence</span>?
              </h2>
              <p className={classes.ctaSubtitle}>
                Let's create a winning digital marketing strategy that drives real results
              </p>
              <a href="#contact-form" className={classes.ctaButton}>Start Your Campaign</a>
            </div>
          </div>
        </section>
      </div>
    </ServicePageLayout>
  );
};

export default DigitalMarketing;
